/**
 * The coach-CHANGE warning (DNKADM-0393 U5) — what the console shows when a reschedule
 * or an Edit-time save comes back `409 STAFF_WILL_CHANGE` (DNKAPI-0546): a dialog, never
 * a toast, in the same idiom as {@link OverrideConflictDialog}.
 *
 * The two hosts that can move a booking's time (`RescheduleDialog` →
 * `PUT /v2/admin/reservations/{uuid}`, `EditReservationDetailsDialog` →
 * `PUT …/{uuid}/details`) catch the refusal through `parseStaffChange` and mount this
 * with what the SERVER said:
 *  - the title is the server's `reason` ("Ryan Perkins is already booked at that time.")
 *    — preferred over `message` because the server substitutes "This coach" there when a
 *    soft-deleted staff row has no `displayName`, so the prose is never a raw id;
 *  - one plain sentence names where the booking would land ("Rescheduling will move this
 *    booking to Zach Boelter."). The lead-in differs per host — a reschedule reschedules,
 *    an Edit-time save saves — and nothing else about the two cases differs.
 *  - **Pick another time** (primary) hands control back to the host's slot picker and
 *    changes nothing. It is also what Esc / a backdrop click do.
 *  - **Move to <coach>** resends the IDENTICAL request with `acknowledgeStaffChange:
 *    true` (`onMoveToProposed`).
 *
 * **`proposedStaff` is read in THREE states, and the last two are not the same fact:**
 *  - a coach → the sentence above, plus **Move to <coach>**;
 *  - **`null`** — the server SAID no coach would be assigned (it sends null exactly when
 *    the service's staff mode is `MEMBER_SELECTS`). That is the spec's second case: say
 *    so ({@link NO_COACH_COPY}) and offer ONLY **Pick another time**;
 *  - **`undefined`** — nothing readable arrived (the `staffChange` payload was absent or
 *    dropped, e.g. by a future edit to `apiErrorFromBody`'s allow-list). The console then
 *    says only what the server did say ({@link unknownCoachCopy}) and still offers only
 *    **Pick another time**. It must NOT print the no-coach line here: that is a factual
 *    claim about the operator's booking which the server never made, and stating it while
 *    hiding a resend the server would have accepted is worse than a neutral degrade.
 *
 * A present coach with a null `displayName` (a soft-deleted staff row — `displayName` is
 * nullable INSIDE a present staff object) degrades to "another coach" in both the
 * sentence and the button: a known id with an unknown name is never shown as an id.
 *
 * Mobile: full-screen below `sm` and the actions wrap — the front desk runs an iPad.
 */
import { useId } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { StaffChange, StaffChangeStaff } from '@/api/reservations';
import { Icon, resolveTokens } from '@/theme';
import { scrubBrand } from '@/utils/whitelabel';
import {
  leadInFor,
  NO_COACH_COPY,
  unknownCoachCopy,
  type StaffChangeAction,
} from './staffChangeCopy';

export interface StaffChangeDialogProps {
  open: boolean;
  change: StaffChange;
  /** The host raising this — a reschedule (`PUT …/{uuid}`) or an Edit time (`PUT …/details`). */
  action: StaffChangeAction;
  /** Primary — back to the host's slot picker, changing nothing. Also Esc / backdrop. */
  onPickAnotherTime: () => void;
  /** Resend the identical request with `acknowledgeStaffChange: true`. Absent from the no-coach case. */
  onMoveToProposed: () => void;
  /** True while the acknowledged resend is in flight — every action is held. */
  submitting?: boolean;
}

/**
 * The coach's name for rendering, or null when the wire has an id but no name (a
 * soft-deleted row). Never falls back to the id — that is the point of returning null.
 */
function coachName(staff: StaffChangeStaff | null): string | null {
  return staff === null ? null : scrubBrand(staff.displayName);
}

export function StaffChangeDialog({
  open,
  change,
  action,
  onPickAnotherTime,
  onMoveToProposed,
  submitting = false,
}: StaffChangeDialogProps) {
  const ids = useId();
  const theme = useTheme();
  const t = resolveTokens(theme);
  const titleId = `${ids}-title`;
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // The server's `reason` carries the prose ("Ryan Perkins is already booked at that
  // time.") with its own "This coach" substitution for a nameless row; `message` is the
  // envelope's summary and only stands in when a reason never arrived.
  const heading = scrubBrand(change.reason) ?? change.message;
  // THREE cases, never two — `null` (the server said "no coach") and `undefined` (the
  // server said nothing readable) are different facts. Collapsing them would print
  // NO_COACH_COPY, a claim about the booking the server never made, whenever the
  // `staffChange` payload failed to reach us.
  const proposed = change.proposedStaff;
  const hasProposed = proposed !== null && proposed !== undefined;
  const movingTo = hasProposed ? coachName(proposed) : null;
  const body = hasProposed
    ? `${leadInFor(action)} will move this booking to ${movingTo ?? 'another coach'}.`
    : proposed === null
      ? NO_COACH_COPY
      : unknownCoachCopy(action);

  function close() {
    if (!submitting) onPickAnotherTime();
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby={titleId}
    >
      <DialogTitle id={titleId} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 1 }}>
        <Icon name="swap_horiz" sx={{ fontSize: 24, color: t.warn, mt: 0.25, flexShrink: 0 }} />
        <Typography
          component="span"
          sx={{ fontSize: 18, fontWeight: 500, overflowWrap: 'anywhere' }}
        >
          {heading}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Typography sx={{ overflowWrap: 'anywhere' }}>{body}</Typography>
      </DialogContent>

      <DialogActions
        sx={{ borderTop: `1px solid ${t.lineSoft}`, px: 2.5, py: 1.5, flexWrap: 'wrap', gap: 1 }}
      >
        {hasProposed && (
          <Button
            variant="outlined"
            color="warning"
            onClick={onMoveToProposed}
            disabled={submitting}
            sx={{ mr: 'auto' }}
          >
            {submitting ? 'Moving…' : `Move to ${movingTo ?? 'another coach'}`}
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={<Icon name="schedule" />}
          onClick={onPickAnotherTime}
          disabled={submitting}
          sx={hasProposed ? undefined : { ml: 'auto' }}
        >
          Pick another time
        </Button>
      </DialogActions>
    </Dialog>
  );
}
